/**
 * Opossum — Nectar-Opossum NPC for the Overgrown Conservatory escort.
 *
 * Canon PR 4 (Conservatory rebuild): the opossum is cycle-phase-aware.
 * In **Irrigation** (cool mist) an awake opossum can be coaxed out by
 * Minna's Purr (`coax`), then follows her by proximity. In **Incubation**
 * (hot/dry) they freeze in a `frozen` cower state — they cannot follow,
 * cannot be captured by nests, and drop out of any active follow chain if
 * the cycle flips under them; once the mist is back, Minna purrs them out
 * again (SPEC-conservatory-rebuild §4, "return to Irrigation and re-purr").
 *
 * State machine:
 *   sleeping → awake (idle) → following → hidden
 *                           ↘ fleeing    ↑
 *                           ↘ frozen ────┘  (Incubation cycle freeze)
 *
 * Visual: small grey capsule. Fades when sleeping or hidden; cower in
 * frozen (ears pinned, eyes wide); nectar pulses when following.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { NPC, NPCConfig } from "./NPC";
import { GroundedNpcPresentation, type GroundedNpcOptions } from "./GroundedNpcPresentation";
import { NPC_TUNING } from "../../config/GameConfig";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";

const OPOSSUM_MODEL_SCALE = 0.44;

export class Opossum extends NPC {
  private presentation: GroundedNpcPresentation | null = null;
  override get species(): string { return "opossum"; }

  private _isSleeping = true;
  private _isHidden = false;
  private _isFrozen = false;
  private scatterTimer = 0;
  private accentMat!: StandardMaterial;
  private eyeMat!: StandardMaterial;
  private nectarMat!: StandardMaterial;
  private accentMeshes: Mesh[] = [];

  constructor(scene: Scene, key: string, position: Vector3, visuals?: GroundedNpcOptions) {
    const config: NPCConfig = {
      key,
      position,
      height: 0.55,
      radius: 0.18,
      color: '#888888',
      behavior: {
        initialState: 'idle',
        calmRadius: 4.0,
        calmThreshold: 1,
        calmRate: 100,
        calmDecay: 0,
        followSpeed: NPC_TUNING.OPOSSUM.FOLLOW_SPEED,
        followDistance: NPC_TUNING.OPOSSUM.FOLLOW_DISTANCE,
      },
      modelPath: visuals?.modelPath ?? "/assets/models/npcs/nectar_opossum.glb",
      managedModelPlacement: !!visuals,
      modelScale: OPOSSUM_MODEL_SCALE,
      modelOffset: new Vector3(0, -0.16, 0),
      modelRotationY: Math.PI / 2,
    };

    super(scene, config);
    this.createVisualShell();
    if (visuals) this.presentation = new GroundedNpcPresentation(this.mesh, this.accentMeshes, OPOSSUM_MODEL_SCALE, visuals);

    // Start sleeping — faded
    this.syncVisualVisibility(this.presentation ? 1 : 0.4);
    this.applyVisualState(0);
  }

  /** Wake the opossum (called when midnight begins). */
  wake(): void {
    this._isSleeping = false;
    this.behavior.setState('idle');
    this.syncVisualVisibility(1);
    this.applyVisualState(0);
  }

  /** Put the opossum to sleep (called when day begins). */
  sleep(): void {
    if (this._isHidden) return; // Don't sleep if safely hidden
    this._isSleeping = true;
    this.behavior.setState('idle');
    this.syncVisualVisibility(this.presentation ? 1 : 0.4);
    this.applyVisualState(0);
  }

  get isSleeping(): boolean {
    return this._isSleeping;
  }

  get isHidden(): boolean {
    return this._isHidden;
  }

  get isFrozen(): boolean {
    return this._isFrozen;
  }

  /**
   * Freeze the opossum in place. Called by NectarEscortPuzzle when the
   * cycle flips to Incubation mid-escort. Drops the opossum out of any
   * active follow chain and emits `OPOSSUM_FROZEN` so the puzzle can
   * count how many of the convoy it just lost.
   */
  freeze(): void {
    if (this._isFrozen) return;
    if (this._isHidden) return;
    this._isFrozen = true;
    this.behavior.followTarget = undefined;
    this.behavior.setState("idle");
    this.scatterTimer = 0;
    this._scatterVelocity = 0;
    gameEvents.emit(GameEvents.OPOSSUM_FROZEN, { npcKey: this.npcKey });
  }

  /**
   * Release the opossum from its frozen state. Called when the cycle
   * flips back to Irrigation. The opossum returns to idle and is
   * re-eligible for follow-by-proximity on the next `checkFollow`.
   */
  unfreeze(): void {
    if (!this._isFrozen) return;
    this._isFrozen = false;
    this.behavior.setState("idle");
  }

  /**
   * Minna's Purr coaxes an awake opossum out of hiding: it starts
   * following her. No-op while it sleeps, cowers (frozen), is hidden,
   * is scattering, or already follows. Returns true when it started.
   */
  coax(minnaX: number, minnaY: number): boolean {
    if (this._isSleeping || this._isHidden || this._isFrozen) return false;
    if (this.scatterTimer > 0) return false;
    if (this.behavior.state === "following") return false;
    this.setFollowTarget({ x: minnaX, y: minnaY });
    // Forensic-QA event: escort convoy attachment. Lets telemetry see
    // when each opossum joined the chain without having to reconstruct
    // it from the generic NPC_STATE_CHANGED stream.
    gameEvents.emit(GameEvents.OPOSSUM_FOLLOWING, { npcKey: this.npcKey });
    return true;
  }

  /**
   * Keep a following opossum on Minna's heels (its follow target tracks
   * her). Proximity alone no longer starts a follow — Minna's Purr does
   * (`coax`). In Incubation the opossum ignores Minna entirely (it's
   * frozen or about to be).
   */
  checkFollow(
    minnaX: number,
    minnaY: number,
    _minnaStealthed: boolean,
    cyclePhase: "incubation" | "irrigation" = "irrigation",
  ): void {
    if (this._isSleeping || this._isHidden || this._isFrozen) return;
    if (this.scatterTimer > 0) return;
    if (cyclePhase === "incubation") return;

    if (this.behavior.state === "following" && this.behavior.followTarget) {
      this.behavior.followTarget = { x: minnaX, y: minnaY };
    }
  }

  /**
   * Scatter when a fox detects this opossum.
   * Runs in a random direction for a short time.
   */
  scatter(): void {
    if (this._isHidden || this._isSleeping || this.scatterTimer > 0) return;

    this.scatterTimer = 1.5; // seconds
    this.behavior.setState('fleeing');

    // Pick random flee direction
    const dir = Math.random() > 0.5 ? 1 : -1;
    if (this.presentation) this.mesh.rotation.y = dir < 0 ? Math.PI : 0;
    // Store direction in follow target X as a hack — the fleeing state
    // in NPCBehavior uses purrX as the threat position, but since we
    // control movement directly here, we'll handle it in update().
    this._scatterVelocity = dir * NPC_TUNING.OPOSSUM.SCATTER_SPEED;

    gameEvents.emit(GameEvents.OPOSSUM_SCATTERED, { npcKey: this.npcKey });
  }

  private _scatterVelocity = 0;

  /**
   * Hide this opossum in a nest (puzzle completion).
   */
  hide(): void {
    this._isHidden = true;
    this.behavior.setState('calmed');

    if (this.presentation) {
      // Occupied burrows provide the safety cue. Retire the walking shell
      // now, including any later import, instead of leaving a pale ghost.
      this.presentation.hide();
    } else {
      // Fade out into nest
      const fadeAnim = new Animation(
        `opossum_${this.npcKey}_hide`,
        "visibility",
        30,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT,
      );
      fadeAnim.setKeys([
        { frame: 0, value: 1 },
        { frame: 15, value: 0.3 },
      ]);
      this.mesh.animations = [fadeAnim];
      this.scene.beginAnimation(this.mesh, 0, 15, false);

    }

    gameEvents.emit(GameEvents.OPOSSUM_HIDDEN, { npcKey: this.npcKey });
  }

  protected override onVisualModelLoaded(): void {
    if (this.visualModel) this.presentation?.adopt(this.visualModel);
  }

  override update(time: number, deltaSeconds: number): void {
    this.updateState(time, deltaSeconds);
    this.presentation?.setPose(this._isFrozen ? "frozen" : this._isSleeping ? "sleep" : null);
    this.presentation?.update(deltaSeconds);
  }

  private updateState(_time: number, deltaSeconds: number): void {
    if (this._isSleeping || this._isHidden) return;

    // Frozen opossums cower in place — no behavior tick, only visuals.
    if (this._isFrozen) {
      this.applyVisualState(_time);
      return;
    }

    // Scatter timer countdown
    if (this.scatterTimer > 0) {
      this.scatterTimer -= deltaSeconds;
      this.mesh.position.x += this._scatterVelocity * deltaSeconds;

      if (this.scatterTimer <= 0) {
        this.scatterTimer = 0;
        this._scatterVelocity = 0;
        this.behavior.setState("idle");
      }
    } else {
      this.updateBehavior(deltaSeconds, false, 0, 0);
    }

    this.applyVisualState(_time);
  }

  override dispose(): void {
    this.presentation?.dispose();
    this.accentMat.dispose();
    this.eyeMat.dispose();
    this.nectarMat.dispose();
    super.dispose();
  }

  private createVisualShell(): void {
    this.accentMat = new StandardMaterial(`opossum_${this.npcKey}_accent`, this.scene);
    this.accentMat.specularColor = Color3.Black();

    this.eyeMat = new StandardMaterial(`opossum_${this.npcKey}_eyes`, this.scene);
    this.eyeMat.diffuseColor = Color3.Black();
    this.eyeMat.specularColor = Color3.Black();

    this.nectarMat = new StandardMaterial(`opossum_${this.npcKey}_nectar`, this.scene);
    this.nectarMat.specularColor = Color3.Black();
    this.nectarMat.alpha = 0.82;

    const bodyCap = MeshBuilder.CreateSphere(
      `opossum_${this.npcKey}_bodyCap`,
      { diameterX: 0.5, diameterY: 0.34, diameterZ: 0.3, segments: 10 },
      this.scene,
    );
    bodyCap.parent = this.mesh;
    bodyCap.position.set(-0.02, 0.08, 0);
    bodyCap.material = this.accentMat;
    this.accentMeshes.push(bodyCap);

    const head = MeshBuilder.CreateSphere(
      `opossum_${this.npcKey}_head`,
      { diameterX: 0.28, diameterY: 0.22, diameterZ: 0.22, segments: 8 },
      this.scene,
    );
    head.parent = this.mesh;
    head.position.set(0.18, 0.1, 0);
    head.material = this.material;
    this.accentMeshes.push(head);

    const nose = MeshBuilder.CreateSphere(
      `opossum_${this.npcKey}_nose`,
      { diameter: 0.05, segments: 6 },
      this.scene,
    );
    nose.parent = this.mesh;
    nose.position.set(0.31, 0.06, 0);
    nose.material = this.accentMat;
    this.accentMeshes.push(nose);

    for (const [index, side] of [-1, 1].entries()) {
      const ear = MeshBuilder.CreateSphere(
        `opossum_${this.npcKey}_ear_${index}`,
        { diameter: 0.08, segments: 6 },
        this.scene,
      );
      ear.parent = this.mesh;
      ear.position.set(0.12, 0.22, side * 0.09);
      ear.material = this.accentMat;
      this.accentMeshes.push(ear);

      const eye = MeshBuilder.CreateSphere(
        `opossum_${this.npcKey}_eye_${index}`,
        { diameter: 0.04, segments: 6 },
        this.scene,
      );
      eye.parent = this.mesh;
      eye.position.set(0.24, 0.11, side * 0.07);
      eye.material = this.eyeMat;
      this.accentMeshes.push(eye);
    }

    const tail = MeshBuilder.CreateCylinder(
      `opossum_${this.npcKey}_tail`,
      { height: 0.52, diameterTop: 0.04, diameterBottom: 0.07, tessellation: 8 },
      this.scene,
    );
    tail.parent = this.mesh;
    tail.position.set(-0.25, 0.05, 0);
    tail.rotation.z = -1.1;
    tail.material = this.accentMat;
    this.accentMeshes.push(tail);

    for (const [index, offsetZ] of [-0.07, 0.07].entries()) {
      const nectarBud = MeshBuilder.CreateSphere(
        `opossum_${this.npcKey}_nectar_${index}`,
        { diameter: 0.08, segments: 6 },
        this.scene,
      );
      nectarBud.parent = this.mesh;
      nectarBud.position.set(-0.06, 0.17, offsetZ);
      nectarBud.material = this.nectarMat;
      this.accentMeshes.push(nectarBud);
    }
  }

  private applyVisualState(time: number): void {
    if (this.visualModel) {
      for (const mesh of this.accentMeshes) {
        mesh.isVisible = false;
      }
      return;
    }

    if (this._isSleeping) {
      this.material.diffuseColor = new Color3(0.5, 0.5, 0.52);
      this.material.emissiveColor = new Color3(0.03, 0.03, 0.04);
      this.accentMat.diffuseColor = new Color3(0.68, 0.59, 0.6);
      this.accentMat.emissiveColor = Color3.Black();
      this.eyeMat.emissiveColor = new Color3(0.02, 0.03, 0.03);
      this.nectarMat.diffuseColor = Color3.FromHexString("#7aa18c");
      this.nectarMat.emissiveColor = new Color3(0.01, 0.03, 0.02);
      return;
    }

    if (this._isFrozen) {
      // Cower — wide pale eyes, muted nectar, no pulse. The player should
      // read this as "this opossum is scared stiff, not sleeping."
      this.material.diffuseColor = Color3.FromHexString("#e0d4c0");
      this.material.emissiveColor = new Color3(0.02, 0.02, 0.02);
      this.accentMat.diffuseColor = Color3.FromHexString("#f6ead4");
      this.accentMat.emissiveColor = Color3.Black();
      this.eyeMat.emissiveColor = new Color3(0.95, 0.9, 0.75);
      this.nectarMat.diffuseColor = Color3.FromHexString("#7fa18a");
      this.nectarMat.emissiveColor = new Color3(0.015, 0.03, 0.02);
      return;
    }

    for (const mesh of this.accentMeshes) {
      mesh.isVisible = true;
    }

    const nectarPulse =
      this.behavior.state === "following"
        ? 0.1 + Math.max(0, Math.sin(time * 7)) * 0.12
        : 0.04;

    switch (this.behavior.state) {
      case 'fleeing':
        this.material.diffuseColor = Color3.FromHexString('#f2b07c');
        this.material.emissiveColor = new Color3(0.1, 0.05, 0.01);
        this.accentMat.diffuseColor = Color3.FromHexString('#f6d4c0');
        this.accentMat.emissiveColor = new Color3(0.03, 0.02, 0.01);
        this.eyeMat.emissiveColor = new Color3(0.85, 0.44, 0.16);
        this.nectarMat.diffuseColor = Color3.FromHexString('#dcb072');
        this.nectarMat.emissiveColor = new Color3(0.16, 0.08, 0.02);
        break;
      case 'following':
        this.material.diffuseColor = Color3.FromHexString('#ddd8cb');
        this.material.emissiveColor = new Color3(0.02, 0.04, 0.03);
        this.accentMat.diffuseColor = Color3.FromHexString('#f7e8da');
        this.accentMat.emissiveColor = new Color3(0.02, 0.03, 0.02);
        this.eyeMat.emissiveColor = new Color3(0.76, 0.9, 0.72);
        this.nectarMat.diffuseColor = Color3.FromHexString('#8dc89d');
        this.nectarMat.emissiveColor = new Color3(0.06 + nectarPulse, 0.14 + nectarPulse, 0.1 + nectarPulse);
        break;
      case 'idle':
      default:
        this.material.diffuseColor = Color3.FromHexString('#cfc8bc');
        this.material.emissiveColor = Color3.Black();
        this.accentMat.diffuseColor = Color3.FromHexString('#f0dece');
        this.accentMat.emissiveColor = Color3.Black();
        this.eyeMat.emissiveColor = new Color3(0.32, 0.42, 0.34);
        this.nectarMat.diffuseColor = Color3.FromHexString('#7ab192');
        this.nectarMat.emissiveColor = new Color3(0.04, 0.08, 0.06);
        break;
    }
  }
}
